// In-app archive article viewer.
//
// Renders the full article inside AAMCirc's admin UI rather than linking
// out to the legacy WordPress membership sites. Pulls the body HTML from
// /api/lab/archive/article/{id}, sanitizes it with DOMPurify (XSS guard
// — strips scripts, event handlers, javascript: URIs), and lays it out
// as a magazine-style read with hero image, dek, badges, and a sidebar
// of metadata (story type, era, tags, AI confidence, audio/trailer
// links if available).

import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import DOMPurify from 'dompurify'
import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import {
  STATUS_CLASS, approveError, publishError, statusLabel, useApproveArticle, usePublishArticle,
} from './usePublishArticle'
import { ScheduleControl } from './ScheduleControl'
import { ExternalIcon, MemberPageLink } from './MemberPageLink'
import { memberArticleUrl } from './memberLink'
import { offerHandBack } from './handBack'
import { usePublicBaseUrl } from '../lib/publicBaseUrls'
import { EDITORIAL_TITLES } from '../editorial/titles'
import { pieceHref, versionHref } from '../editorial/links'
import { openEdit } from '../editorial/api'
import { APPROVAL } from '../editorial/labels'
import { canPublish as mayApprove, useEditorialMe } from '../editorial/me'
import { describeError } from '../lib/describeError'
import { useVersionsOn } from '../editorial/useVersionsOn'

const PUB_NAMES: Record<string, string> = {
  rc_car_action: 'RC Car Action',
  flight_journal: 'Flight Journal',
  model_airplane_news: 'Model Airplane News',
  diecast: 'Die Cast X',
}

const PUB_ACCENT: Record<string, string> = {
  rc_car_action: 'text-radar-red',
  flight_journal: 'text-amber-glow',
  model_airplane_news: 'text-amber-glow',
  diecast: 'text-radar-violet',
}

type ArticleDetail = {
  id: number
  publication_slug: string
  site_type: string
  title: string
  slug: string | null
  wp_url: string
  wp_post_id: number
  post_date: string | null
  post_modified: string | null
  featured_image_url: string | null
  story_type: string | null
  era: string | null
  ai_tags: string[] | null
  short_teaser: string | null
  long_teaser: string | null
  ai_confidence: number | null
  ai_model: string | null
  editor_priority: number
  engagement_score: number
  enriched: boolean
  embedded: boolean
  post_content_html: string | null
  post_content_text: string | null
  audio_url: string | null
  audio_duration_seconds: number | null
  trailer_url: string | null
  trailer_poster_url: string | null
  // Per-article SEO (Track B v2 Phase A)
  seo_title: string | null
  seo_description: string | null
  seo_canonical_url: string | null
  seo_keywords: string[] | null
  seo_locked_fields: string[]
  // 'active' = live in the membership, 'excluded' = unpublished by an
  // editor, 'stale' = the WordPress post is gone.
  status: string
  unpublished_at: string | null
  unpublished_by: string | null
  unpublish_reason: string | null
  publish_at: string | null
  publish_scheduled_by: string | null
  publish_schedule_error: string | null
  // An editor's sign-off, needed before Publish or Schedule…; any content edit clears it.
  approved_at?: string | null
  approved_by?: string | null
  // The issue the article ran in. The sync resolves it from WordPress'
  // issue label; `digital_edition_manual` means an editor set it here.
  wp_article_category: string | null
  digital_edition_id: number | null
  digital_edition_manual: boolean
  digital_edition_title: string | null
  // The desk holds the row (the WordPress sync leaves it alone); can_edit: Edit is offered.
  desk_owned: boolean
  can_edit: boolean
  // Hand back to WordPress is offered (false for old-posts imports and new-site articles).
  can_hand_back: boolean
}

type IssueOption = {
  id: number | null
  title: string | null
  post_date: string | null
  status?: string
}

// Publish/unpublish control. `status` is the single membership visibility
// switch: flipping to 'excluded' pulls the article from the Briefing rails,
// search, related-article slots and its own member-facing detail page at
// once. Unpublishing asks first because it is member-visible; putting an
// article back is harmless and applies immediately.
function MemberPageLinkRow({ article }: { article: ArticleDetail }) {
  const base = usePublicBaseUrl(article.publication_slug)
  const url = memberArticleUrl(base, article.slug)
  if (!url) return null
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-1.5 font-mono text-[10px] tracking-label uppercase text-paper-trace hover:text-amber-glow"
    >
      <ExternalIcon />
      View on the member site
    </a>
  )
}

function PublishPanel({ article }: { article: ArticleDetail }) {
  const [confirming, setConfirming] = useState(false)
  const live = article.status === 'active'
  const stale = article.status === 'stale'

  const flip = usePublishArticle(article.id)
  useEffect(() => { if (flip.isSuccess) setConfirming(false) }, [flip.isSuccess])
  const err = publishError(flip.error)
  const me = useEditorialMe()
  const { approve, unapprove } = useApproveArticle(article.id)
  const approvalErr = approveError(approve.error ?? unapprove.error)
  const BTN = 'font-mono text-[11px] tracking-label uppercase border px-2.5 py-1 disabled:opacity-50'

  return (
    <Panel title="Membership">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span
            className={`inline-block w-1.5 h-1.5 rounded-full ${
              live ? 'bg-radar-green' : stale ? 'bg-paper-trace' : 'bg-radar-red'
            }`}
          />
          <span className="font-mono text-[11px] tracking-label uppercase text-paper">
            {live ? 'Published' : stale ? 'Gone from WordPress' : 'Unpublished'}
          </span>
        </div>

        <MemberPageLinkRow article={article} />

        <p className="font-mono text-[10px] leading-[1.5] text-paper-trace">
          {live
            ? 'Visible to members across the site.'
            : stale
              ? 'The WordPress post no longer exists, so there is nothing to publish. Restore it in WordPress and re-sync.'
              : 'Hidden from members everywhere \u2014 briefing, search and its own page.'}
        </p>

        {!live && !stale && article.unpublished_at && (
          <div className="font-mono text-[10px] text-paper-trace border-l border-ink-500 pl-2">
            Unpublished {fmtDate(article.unpublished_at)}
            {article.unpublished_by ? ` by ${article.unpublished_by}` : ''}
          </div>
        )}

        {err && <div className="font-mono text-[10px] text-radar-red">{err}</div>}

        {article.status === 'excluded' && (
          <div className="space-y-1.5">
            {article.approved_at ? (
              <div className="flex flex-wrap items-center gap-2 font-mono text-[10px] text-radar-green">
                <span>
                  {APPROVAL.chip}
                  {article.approved_by ? ` by ${article.approved_by}` : ''} {fmtDate(article.approved_at)}
                </span>
                {mayApprove(me.data) && (
                  <button onClick={() => unapprove.mutate()} disabled={unapprove.isPending}
                          className="uppercase tracking-label text-paper-trace hover:text-paper disabled:opacity-50">
                    {APPROVAL.undo}
                  </button>
                )}
              </div>
            ) : mayApprove(me.data) ? (
              <button onClick={() => approve.mutate()} disabled={approve.isPending}
                      className={`${BTN} border-radar-green/60 text-radar-green hover:bg-radar-green/10`}>
                {approve.isPending ? 'Working\u2026' : APPROVAL.approve}
              </button>
            ) : me.data ? (
              <span className="font-mono text-[10px] text-paper-trace">{APPROVAL.waitingArticle}</span>
            ) : null}
            {approvalErr && <div className="font-mono text-[10px] text-radar-red">{approvalErr}</div>}
            <ScheduleControl
              id={article.id}
              publishAt={article.publish_at}
              lastError={article.publish_schedule_error}
            />
            {article.publish_at && article.publish_scheduled_by && (
              <div className="font-mono text-[10px] text-paper-trace">
                Scheduled by {article.publish_scheduled_by}
              </div>
            )}
          </div>
        )}

        {stale ? null : confirming ? (
          <div className="space-y-2">
            <p className="font-mono text-[10px] leading-[1.5] text-amber-glow">
              Hide this article from members?
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => flip.mutate(false)}
                disabled={flip.isPending}
                className="font-mono text-[11px] tracking-label uppercase border border-radar-red/60 text-radar-red px-2.5 py-1 hover:bg-radar-red/10 disabled:opacity-50"
              >
                {flip.isPending ? 'Working\u2026' : 'Confirm'}
              </button>
              <button
                onClick={() => setConfirming(false)}
                disabled={flip.isPending}
                className="font-mono text-[11px] tracking-label uppercase border border-ink-500 text-paper-trace px-2.5 py-1 hover:text-paper disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => (live ? setConfirming(true) : flip.mutate(true))}
            disabled={flip.isPending}
            className={`font-mono text-[11px] tracking-label uppercase border px-2.5 py-1 disabled:opacity-50 ${
              live
                ? 'border-ink-500 text-paper-trace hover:text-paper hover:border-paper-trace'
                : 'border-radar-green/60 text-radar-green hover:bg-radar-green/10'
            }`}
          >
            {flip.isPending ? 'Working\u2026' : live ? 'Unpublish' : 'Publish'}
          </button>
        )}
      </div>
    </Panel>
  )
}

const HAND_BACK_CONFIRM = "The next WordPress change to this post will replace the desk's version."

// Desk panel: Edit (native titles, live rows) and, for admins, handing a desk-edited
// WordPress article back to the sync.
function DeskPanel({ article }: { article: ArticleDetail }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const me = useEditorialMe()
  const section = EDITORIAL_TITLES.find((t) => t.slug === article.publication_slug)
  const [confirming, setConfirming] = useState(false)
  const BTN = 'font-mono text-[11px] tracking-label uppercase border px-2.5 py-1 disabled:opacity-50'

  const edit = useMutation({
    mutationFn: () => openEdit(article.id),
    onSuccess: (r) => { if (section) navigate(pieceHref(section.path, r.draft_id)) },
  })
  const handBack = useMutation({
    mutationFn: () => api.post<ArticleDetail>(`/api/lab/archive/article/${article.id}/hand-back`),
    onSuccess: () => {
      setConfirming(false)
      qc.invalidateQueries({ queryKey: ['archive-article', article.id] })
      qc.invalidateQueries({ queryKey: ['lab', 'archive'] })
    },
  })
  const isAdmin = me.data?.is_admin === true
  const canHandBack = offerHandBack(article, isAdmin)
  const showEdit = article.can_edit && !!section
  if (!article.desk_owned && !showEdit) return null
  const err = edit.error ? describeError(edit.error) : handBack.error ? describeError(handBack.error) : null

  return (
    <Panel title="Desk">
      <div className="space-y-3">
        {article.desk_owned && (
          <span className="inline-block font-mono text-[10px] tracking-label uppercase border border-amber-glow/60 text-amber-glow px-2 py-1">
            Edited in the desk
          </span>
        )}
        {err && <div className="font-mono text-[10px] text-radar-red">{err}</div>}
        {confirming ? (
          <div className="space-y-2">
            <p className="font-mono text-[10px] leading-[1.5] text-amber-glow">{HAND_BACK_CONFIRM}</p>
            <div className="flex gap-2">
              <button onClick={() => handBack.mutate()} disabled={handBack.isPending}
                className={`${BTN} border-radar-red/60 text-radar-red hover:bg-radar-red/10`}>
                {handBack.isPending ? 'Working\u2026' : 'Confirm'}
              </button>
              <button onClick={() => setConfirming(false)} disabled={handBack.isPending}
                className={`${BTN} border-ink-500 text-paper-trace hover:text-paper`}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {showEdit && (
              <button onClick={() => edit.mutate()} disabled={edit.isPending}
                className={`${BTN} border-amber-glow/60 text-amber-glow hover:bg-amber-glow/10`}>
                {edit.isPending ? 'Opening\u2026' : 'Edit'}
              </button>
            )}
            {canHandBack && (
              <button onClick={() => { handBack.reset(); setConfirming(true) }}
                className={`${BTN} border-ink-500 text-paper-trace hover:text-paper hover:border-paper-trace`}>
                Hand back to WordPress
              </button>
            )}
          </div>
        )}
      </div>
    </Panel>
  )
}

// Which magazine issue the article ran in. The sync links it from the
// WordPress issue label; an editor can pick another issue, say it is not
// from an issue, or go back to the label. A hand-set link survives the
// nightly sync.
function IssuePanel({ article }: { article: ArticleDetail }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [picked, setPicked] = useState<string>('')

  const issues = useQuery({
    queryKey: ['lab', 'issues', 'list', article.publication_slug, ''],
    queryFn: () => api.get<{ items: IssueOption[] }>(
      `/api/lab/issues?publication_slug=${article.publication_slug}`,
    ),
    enabled: editing,
    staleTime: 5 * 60_000,
  })
  const options = useMemo(
    () => (issues.data?.items ?? [])
      .filter((i) => i.id != null && i.status !== 'deleted')
      .sort((a, b) => (b.post_date ?? '').localeCompare(a.post_date ?? '')),
    [issues.data],
  )

  const save = useMutation({
    mutationFn: (body: { edition_id: number | null } | { automatic: true }) =>
      api.put<ArticleDetail>(`/api/lab/archive/article/${article.id}/edition`, body),
    onSuccess: () => {
      setEditing(false)
      qc.invalidateQueries({ queryKey: ['archive-article', article.id] })
      qc.invalidateQueries({ queryKey: ['lab', 'archive'] })
    },
  })
  const detail = (save.error as { detail?: unknown } | null)?.detail
  const err = save.error
    ? (typeof detail === 'string' ? detail : 'Could not change the issue.')
    : null

  const startEditing = () => {
    setPicked(article.digital_edition_id != null ? String(article.digital_edition_id) : '')
    setEditing(true)
  }

  const BTN = 'font-mono text-[11px] tracking-label uppercase border px-2.5 py-1 disabled:opacity-50'

  return (
    <Panel title="Issue">
      <div className="space-y-2">
        <div className="font-mono text-[11px] text-paper">
          {article.digital_edition_id != null ? (
            <Link
              to={`/lab/issues/${article.digital_edition_id}`}
              className="hover:text-amber-glow underline decoration-dotted"
            >
              {article.digital_edition_title ?? `Issue #${article.digital_edition_id}`}
            </Link>
          ) : (
            <span className="text-paper-trace">Not linked to an issue</span>
          )}
        </div>
        <div className="font-mono text-[10px] leading-[1.5] text-paper-trace">
          {article.digital_edition_manual
            ? 'Set by hand \u2014 the sync leaves it alone.'
            : article.wp_article_category
              ? `From the WordPress label \u201c${article.wp_article_category}\u201d.`
              : 'No WordPress issue label.'}
          {article.digital_edition_manual && article.wp_article_category && (
            <> WordPress label: “{article.wp_article_category}”.</>
          )}
        </div>

        {err && <div className="font-mono text-[10px] text-radar-red">{err}</div>}

        {editing ? (
          <div className="space-y-2">
            <select
              value={picked}
              onChange={(e) => setPicked(e.target.value)}
              disabled={issues.isLoading}
              className="w-full bg-paper-deepest border border-paper-trace px-2 py-1 font-mono text-[11px] text-paper"
            >
              <option value="">
                {issues.isLoading ? 'Loading issues\u2026' : 'Not from an issue'}
              </option>
              {options.map((i) => (
                <option key={i.id} value={String(i.id)}>
                  {i.title ?? `Issue #${i.id}`}
                </option>
              ))}
            </select>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => save.mutate({ edition_id: picked ? Number(picked) : null })}
                disabled={save.isPending || issues.isLoading}
                className={`${BTN} border-amber-glow/60 text-amber-glow hover:bg-amber-glow/10`}
              >
                {save.isPending ? 'Working\u2026' : 'Save'}
              </button>
              <button
                onClick={() => setEditing(false)}
                disabled={save.isPending}
                className={`${BTN} border-ink-500 text-paper-trace hover:text-paper`}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={startEditing}
              className={`${BTN} border-ink-500 text-paper-trace hover:text-paper hover:border-paper-trace`}
            >
              Change issue
            </button>
            {article.digital_edition_manual && (
              <button
                onClick={() => save.mutate({ automatic: true })}
                disabled={save.isPending}
                className={`${BTN} border-ink-500 text-paper-trace hover:text-paper hover:border-paper-trace`}
                title="Drop the hand-set issue and link from the WordPress label again"
              >
                {save.isPending ? 'Working\u2026' : 'Use WordPress label'}
              </button>
            )}
          </div>
        )}
      </div>
    </Panel>
  )
}

function ArticleSeoPanel({ article }: { article: ArticleDetail }) {
  const qc = useQueryClient()
  const [title, setTitle] = useState(article.seo_title ?? '')
  const [desc, setDesc] = useState(article.seo_description ?? '')
  const [kw, setKw] = useState((article.seo_keywords ?? []).join(', '))

  // Sync local state if the article changes (re-fetch / different article).
  useEffect(() => {
    setTitle(article.seo_title ?? '')
    setDesc(article.seo_description ?? '')
    setKw((article.seo_keywords ?? []).join(', '))
  }, [article.id, article.seo_title, article.seo_description, article.seo_keywords])

  const locked = new Set(article.seo_locked_fields ?? [])

  const save = useMutation({
    mutationFn: (body: {
      seo_title?: string; seo_description?: string;
      seo_keywords?: string[]; unlock_fields?: string[];
    }) => api.patch<ArticleDetail>(`/api/lab/archive/article/${article.id}/seo`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['archive-article', article.id] })
    },
  })

  const onSave = () => {
    save.mutate({
      seo_title: title.trim() || undefined,
      seo_description: desc.trim() || undefined,
      seo_keywords: kw
        .split(',').map((s) => s.trim()).filter(Boolean),
    })
  }

  const onUnlock = (field: string) => {
    save.mutate({ unlock_fields: [field] })
  }

  return (
    <div className="border border-ink-500 bg-ink-800/40 p-3 space-y-3">
      <div className="font-mono text-[11px] tracking-label text-amber-glow uppercase">
        SEO metadata
      </div>
      <p className="font-mono text-[10px] text-paper-trace leading-relaxed">
        Agent auto-fills these. Editing a field locks it from the next agent run.
      </p>

      <label className="block">
        <div className="font-mono text-[11px] text-paper-trace mb-1 flex items-center gap-1.5">
          <span>SEO title</span>
          {locked.has('seo_title') && (
            <button onClick={() => onUnlock('seo_title')}
                    className="text-[9px] text-amber-glow hover:underline"
                    title="Unlock — let agent re-curate">
              🔒 unlock
            </button>
          )}
        </div>
        <input
          type="text" value={title}
          onChange={(e) => setTitle(e.target.value.slice(0, 120))}
          className="w-full bg-ink-700 border border-ink-500 px-2 py-1 text-paper rounded text-sm"
        />
      </label>

      <label className="block">
        <div className="font-mono text-[11px] text-paper-trace mb-1 flex items-center gap-1.5">
          <span>SEO description</span>
          {locked.has('seo_description') && (
            <button onClick={() => onUnlock('seo_description')}
                    className="text-[9px] text-amber-glow hover:underline">
              🔒 unlock
            </button>
          )}
        </div>
        <textarea
          value={desc}
          onChange={(e) => setDesc(e.target.value.slice(0, 220))}
          rows={3}
          className="w-full bg-ink-700 border border-ink-500 px-2 py-1 text-paper rounded text-sm"
        />
      </label>

      <label className="block">
        <div className="font-mono text-[11px] text-paper-trace mb-1 flex items-center gap-1.5">
          <span>Keywords (comma-separated)</span>
          {locked.has('seo_keywords') && (
            <button onClick={() => onUnlock('seo_keywords')}
                    className="text-[9px] text-amber-glow hover:underline">
              🔒 unlock
            </button>
          )}
        </div>
        <textarea
          value={kw}
          onChange={(e) => setKw(e.target.value.slice(0, 1000))}
          rows={3}
          className="w-full bg-ink-700 border border-ink-500 px-2 py-1 text-paper rounded text-sm"
        />
      </label>

      <button onClick={onSave} disabled={save.isPending}
              className="bg-amber-glow text-ink-900 font-mono text-[11px] px-3 py-1 rounded">
        {save.isPending ? 'Saving…' : 'Save & lock'}
      </button>
      {save.isError && (
        <div className="text-[10px] text-radar-red font-mono">
          {String(save.error)}
        </div>
      )}
    </div>
  )
}

function fmtDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

function fmtDuration(s: number | null): string {
  if (!s) return ''
  const m = Math.floor(s / 60)
  const r = Math.round(s % 60)
  return `${m}:${r.toString().padStart(2, '0')}`
}

export function LabArchiveArticleViewPage() {
  const { id } = useParams<{ id: string }>()
  const articleId = id ? parseInt(id, 10) : null

  const { data, isLoading, error } = useQuery<ArticleDetail>({
    queryKey: ['archive-article', articleId],
    queryFn: () => api.get<ArticleDetail>(`/api/lab/archive/article/${articleId}`),
    enabled: articleId != null,
    staleTime: 60_000,
  })
  const versionsOn = useVersionsOn(data?.publication_slug)

  // Sanitize HTML through DOMPurify before any insertion. DOMPurify
  // strips <script>, event-handler attributes, and javascript: URIs by
  // default — XSS-safe even though the body originated outside our
  // codebase (WordPress export). We allow target/rel attrs so any
  // remaining anchor tags work, but we don't relax the script policy.
  const sanitizedHtml = useMemo(() => {
    if (!data?.post_content_html) return ''
    return DOMPurify.sanitize(data.post_content_html, {
      ADD_ATTR: ['target', 'rel'],
    })
  }, [data?.post_content_html])

  if (isLoading) {
    return <div className="p-8 label text-paper-trace">Loading article…</div>
  }
  if (error || !data) {
    return (
      <div className="p-8 space-y-3">
        <Link to="/lab/archive" className="label-amber hover:underline">← Archive</Link>
        <div className="label text-radar-red">Article not found.</div>
      </div>
    )
  }

  const pubName = PUB_NAMES[data.publication_slug] ?? data.publication_slug
  const editorialTitle = EDITORIAL_TITLES.find((t) => t.slug === data.publication_slug)
  const accent = PUB_ACCENT[data.publication_slug] ?? 'text-amber-glow'

  return (
    <div className="p-8 space-y-6 max-w-[1200px]">
      <div className="flex items-center gap-3">
        <Link to="/lab/archive" className="label-amber hover:underline">← Archive</Link>
        <span className="label text-paper-trace">/</span>
        <Link to={`/lab/archive?pub=${data.publication_slug}`} className="label-amber hover:underline">
          {pubName}
        </Link>
        <span className="label text-paper-trace">/</span>
        <span className="label">Article #{data.id}</span>
        <MemberPageLink
          publicationSlug={data.publication_slug}
          slug={data.slug}
          status={data.status}
        />
        {versionsOn && editorialTitle && (
          <Link to={versionHref(editorialTitle.path, { articleId: data.id })} className="label-amber hover:underline ml-auto">
            Make a version
          </Link>
        )}
      </div>

      <header className="border-b border-ink-500 pb-6 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`label ${accent}`}>{pubName}</span>
          {data.story_type && (
            <span className="font-mono text-[11px] tracking-label text-radar-cyan uppercase">
              {data.story_type.replace(/_/g, ' ')}
            </span>
          )}
          {data.era && (
            <span className="font-mono text-[11px] tracking-label text-paper-trace uppercase">
              · {data.era}
            </span>
          )}
          {data.post_date && (
            <span className="font-mono text-[11px] text-paper-trace">
              · {fmtDate(data.post_date)}
            </span>
          )}
        </div>
        <h1 className="font-serif text-[44px] leading-[1.05] text-paper">
          {data.status !== 'active' && (
            <span
              className={`align-middle mr-3 font-mono text-[11px] tracking-label uppercase border px-2 py-1 ${
                STATUS_CLASS[data.status] ?? 'border-ink-500 text-paper-trace'
              }`}
            >
              {statusLabel(data.status, data.unpublish_reason)}
            </span>
          )}
          {data.title}
        </h1>
        {data.short_teaser && (
          <p className="font-serif text-[18px] leading-[1.5] text-paper-dim italic max-w-[800px]">
            {data.short_teaser}
          </p>
        )}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8">
        <main className="space-y-6">
          {data.featured_image_url && (
            <figure className="border border-ink-500 bg-ink-800/40">
              <img
                src={data.featured_image_url}
                alt={data.title}
                className="w-full h-auto"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
              />
            </figure>
          )}

          {data.long_teaser && (
            <div className="border-l-2 border-amber-glow/40 pl-4 font-serif text-[16px] leading-[1.6] text-paper">
              {data.long_teaser}
            </div>
          )}

          {sanitizedHtml ? (
            <article
              className="archive-article-body font-serif text-[17px] leading-[1.7] text-paper"
              // sanitizedHtml is the output of DOMPurify.sanitize() above —
              // not raw user input. DOMPurify strips <script>, on*
              // handlers, javascript: URIs.
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          ) : (
            <div className="label text-paper-trace italic p-6 border border-ink-500 bg-ink-800/40">
              No article body available. Sync may not have populated this row,
              or the article was archived.
            </div>
          )}
        </main>

        <aside className="space-y-4">
          <PublishPanel article={data} />

          <DeskPanel article={data} />

          <IssuePanel article={data} />

          {data.trailer_url && (
            <Panel title="Trailer">
              {data.trailer_poster_url ? (
                <video
                  src={data.trailer_url}
                  poster={data.trailer_poster_url}
                  controls
                  className="w-full border border-ink-500"
                />
              ) : (
                <video src={data.trailer_url} controls className="w-full border border-ink-500" />
              )}
            </Panel>
          )}

          {data.audio_url && (
            <Panel title="Audio narration">
              <audio src={data.audio_url} controls className="w-full" />
              {data.audio_duration_seconds && (
                <div className="font-mono text-[11px] text-paper-trace mt-1">
                  {fmtDuration(data.audio_duration_seconds)}
                </div>
              )}
            </Panel>
          )}

          {data.ai_tags && data.ai_tags.length > 0 && (
            <Panel title="Tags">
              <div className="flex flex-wrap gap-1.5">
                {data.ai_tags.map((t) => (
                  <span
                    key={t}
                    className="font-mono text-[10px] text-paper-trace border border-ink-500 px-1.5 py-0.5"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </Panel>
          )}

          <ArticleSeoPanel article={data} />

          <Panel title="Metadata">
            <dl className="space-y-1.5 font-mono text-[11px]">
              <Row label="ID">{data.id}</Row>
              <Row label="Slug">{data.slug || '—'}</Row>
              <Row label="WP post id">{data.wp_post_id}</Row>
              <Row label="Site type">{data.site_type}</Row>
              {data.post_modified && data.post_modified !== data.post_date && (
                <Row label="Modified">{fmtDate(data.post_modified)}</Row>
              )}
              {data.ai_confidence != null && (
                <Row label="AI conf.">
                  <span
                    className={
                      data.ai_confidence >= 0.85 ? 'text-radar-green'
                      : data.ai_confidence >= 0.6 ? 'text-amber-glow'
                      : 'text-radar-violet'
                    }
                  >
                    {data.ai_confidence.toFixed(2)}
                  </span>
                </Row>
              )}
              {data.ai_model && <Row label="AI model">{data.ai_model}</Row>}
              <Row label="Enriched">{data.enriched ? '✓' : '—'}</Row>
              <Row label="Embedded">{data.embedded ? '✓' : '—'}</Row>
              <Row label="Editor priority">{data.editor_priority}</Row>
              <Row label="Engagement">{data.engagement_score.toFixed(2)}</Row>
            </dl>
          </Panel>
        </aside>
      </div>

      {/* Magazine-prose styling for the sanitized article body. */}
      <style>{`
        .archive-article-body p { margin: 0 0 1.1em 0; }
        .archive-article-body h1, .archive-article-body h2, .archive-article-body h3,
        .archive-article-body h4 { font-family: Georgia, 'Times New Roman', serif;
          color: #f4f1ea; margin: 1.6em 0 0.6em 0; line-height: 1.2; }
        .archive-article-body h2 { font-size: 28px; }
        .archive-article-body h3 { font-size: 22px; }
        .archive-article-body h4 { font-size: 18px; }
        .archive-article-body img { max-width: 100%; height: auto; margin: 1.4em auto;
          display: block; border: 1px solid rgba(212, 168, 73, 0.18); }
        .archive-article-body figure { margin: 1.4em 0; }
        .archive-article-body figcaption { font-size: 12px; color: #9aa3ad;
          font-style: italic; text-align: center; margin-top: 0.4em; }
        .archive-article-body blockquote { border-left: 3px solid rgba(212, 168, 73, 0.6);
          padding: 0.4em 1.2em; margin: 1.4em 0; font-style: italic; color: #c9d1d9; }
        .archive-article-body ul, .archive-article-body ol { margin: 0 0 1.1em 1.5em; }
        .archive-article-body li { margin-bottom: 0.4em; }
        .archive-article-body a { color: #d4a849; text-decoration: underline; }
        .archive-article-body a:hover { color: #f4cf7e; }
        .archive-article-body code { font-family: 'JetBrains Mono', monospace;
          background: rgba(255, 255, 255, 0.05); padding: 0.1em 0.4em;
          border-radius: 2px; font-size: 0.9em; }
        .archive-article-body pre { background: rgba(0, 0, 0, 0.4); padding: 1em;
          border: 1px solid rgba(255, 255, 255, 0.08); overflow-x: auto;
          margin: 1.4em 0; }
        .archive-article-body hr { border: 0; border-top: 1px solid rgba(255, 255, 255, 0.1);
          margin: 2em 0; }
        .archive-article-body table { border-collapse: collapse; margin: 1.4em 0; width: 100%; }
        .archive-article-body th, .archive-article-body td {
          border: 1px solid rgba(255, 255, 255, 0.1); padding: 0.5em 0.75em;
          text-align: left; }
      `}</style>
    </div>
  )
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-ink-500 bg-ink-800/40 p-4">
      <div className="label text-paper-faint mb-2">{title}</div>
      {children}
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-ink-500/30 pb-1 last:border-b-0 last:pb-0">
      <span className="text-paper-faint">{label}</span>
      <span className="text-paper-dim text-right">{children}</span>
    </div>
  )
}
